import React from 'react'
import { usePermission } from '../hooks/usePermission'
import { useMembership } from '../hooks/useMembership'
import Hint from './common/Hint'

/**
 * 許可權守衛元件
 * 根據許可權控制子元件的渲染
 */
export const PermissionGuard = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  showFallback = false,
  children
}) => {
  const { can, canAny, canAll } = usePermission()

  let hasPermission = false

  if (permission) {
    // 單個許可權檢查
    hasPermission = can(permission)
  } else if (permissions && Array.isArray(permissions)) {
    // 多個許可權檢查
    hasPermission = requireAll ? canAll(permissions) : canAny(permissions)
  } else {
    // 沒有指定許可權，直接返回 children
    return <>{children}</>
  }

  if (!hasPermission) {
    if (showFallback) {
      return (
        fallback || (
          <Hint
            title='許可權不足'
            tone='warning'
          >
            您沒有許可權訪問此功能
          </Hint>
        )
      )
    }
    return null
  }

  return <>{children}</>
}

/**
 * 角色守衛元件
 * 根據角色控制子元件的渲染
 */
export const RoleGuard = ({ roles, fallback = null, children }) => {
  const { businessRole } = usePermission()

  if (!roles || !Array.isArray(roles)) {
    return <>{children}</>
  }

  const hasRole = roles.includes(businessRole)

  if (!hasRole) {
    return fallback
  }

  return <>{children}</>
}

/**
 * Owner 守衛元件
 * 只有擁有者可以看到
 */
export const OwnerOnly = ({ fallback = null, children }) => {
  const { isBusinessOwner } = usePermission()

  if (!isBusinessOwner()) {
    return fallback
  }

  return <>{children}</>
}

/**
 * 會員方案守衛元件
 * 根據會員方案控制子元件的渲染
 */
export const PlanGuard = ({
  feature,
  plans,
  fallback = null,
  showFallback = false,
  children
}) => {
  const { hasFeature, isPlan } = useMembership()

  let hasAccess = false

  if (feature) {
    // 通過功能名稱檢查
    hasAccess = hasFeature(feature)
  } else if (plans) {
    // 通過方案名稱檢查
    const planArray = Array.isArray(plans) ? plans : [plans]
    hasAccess = isPlan(planArray)
  } else {
    // 沒有指定條件，直接返回 children
    return <>{children}</>
  }

  if (!hasAccess) {
    if (showFallback) {
      return (
        fallback || (
          <Hint
            title='功能限制'
            tone='warning'
          >
            此功能需要升級會員方案才能使用
          </Hint>
        )
      )
    }
    return null
  }

  return <>{children}</>
}
